import type { RQLToken, RQLASTNode } from "./types";
import { RQLParserError } from "./types";
import { RQL_FIELD_DEFINITIONS } from "./fieldDefinitions";

export function parse(tokens: RQLToken[]): RQLASTNode {
  if (tokens.length === 0) {
    throw new RQLParserError("Empty query");
  }

  const parser = new Parser(tokens);
  const ast = parser.parseExpression();

  if (!parser.isAtEnd()) {
    const currentToken = parser.getCurrent();
    throw new RQLParserError(
      `Unexpected token: ${currentToken.value}`,
      currentToken.position,
    );
  }

  return ast;
}

class Parser {
  private tokens: RQLToken[];
  private currentIndex: number = 0;

  constructor(tokens: RQLToken[]) {
    this.tokens = tokens;
  }

  parseExpression(): RQLASTNode {
    return this.parseOr();
  }

  private parseOr(): RQLASTNode {
    let expr = this.parseAnd();

    while (this.match("operator", "or")) {
      const right = this.parseAnd();
      expr = {
        type: "logical",
        operator: "or",
        left: expr,
        right,
      };
    }

    return expr;
  }

  private parseAnd(): RQLASTNode {
    let expr = this.parsePrimary();

    while (this.match("operator", "and")) {
      const right = this.parsePrimary();
      expr = {
        type: "logical",
        operator: "and",
        left: expr,
        right,
      };
    }

    return expr;
  }

  private parsePrimary(): RQLASTNode {
    if (this.match("parenthesis", "(")) {
      const expr = this.parseExpression();
      if (!this.match("parenthesis", ")")) {
        const token = this.isAtEnd() ? this.previous() : this.getCurrent();
        throw new RQLParserError(
          "Expected closing parenthesis",
          token.position,
        );
      }
      return {
        type: "group",
        expression: expr,
      };
    }

    return this.parseComparison();
  }

  private parseComparison(): RQLASTNode {
    if (!this.check("field")) {
      const token = this.getCurrent();
      throw new RQLParserError(
        `Expected field name, got: ${token.value}`,
        token.position,
      );
    }

    const fieldToken = this.advance();
    const fieldName = fieldToken.value;
    const fieldDef = RQL_FIELD_DEFINITIONS[fieldName];

    if (!fieldDef) {
      throw new RQLParserError(
        `Unknown field: ${fieldName}`,
        fieldToken.position,
      );
    }

    if (!this.check("operator")) {
      const token = this.getCurrent();
      throw new RQLParserError(
        `Expected comparison operator after field '${fieldName}'`,
        token.position,
      );
    }

    const operatorToken = this.advance();
    const operator = operatorToken.value;

    if (!["=", "!=", ">", ">=", "<", "<="].includes(operator)) {
      throw new RQLParserError(
        `Invalid comparison operator: ${operator}`,
        operatorToken.position,
      );
    }

    if (!this.check("value")) {
      const token = this.getCurrent();
      const position = token
        ? token.position
        : (this.tokens[this.tokens.length - 1]?.position ?? 0);
      throw new RQLParserError(
        `Expected value after operator '${operator}'`,
        position,
      );
    }

    const valueToken = this.advance();
    const rawValue = valueToken.value;

    const parsedValue = this.parseAndValidateValue(
      fieldDef,
      rawValue,
      valueToken.position,
    );

    return {
      type: "comparison",
      field: fieldName,
      operator: operator as any,
      value: parsedValue,
    };
  }

  private parseAndValidateValue(
    fieldDef: any,
    rawValue: string,
    position: number,
  ): string | number {
    if (fieldDef.type === "number") {
      const numValue = parseFloat(rawValue);
      if (isNaN(numValue)) {
        throw new RQLParserError(
          `Invalid number value for field '${fieldDef.name}': ${rawValue}`,
          position,
        );
      }
      return numValue;
    }

    if (fieldDef.type === "string") {
      if (
        fieldDef.allowedValues &&
        !fieldDef.allowedValues.includes(rawValue)
      ) {
        throw new RQLParserError(
          `Invalid value '${rawValue}' for field '${fieldDef.name}'. Allowed values: ${fieldDef.allowedValues.join(", ")}`,
          position,
        );
      }
      return rawValue;
    }

    if (fieldDef.type === "boolean") {
      const lowerValue = rawValue.toLowerCase();
      if (lowerValue === "true" || lowerValue === "false") {
        return lowerValue === "true" ? 1 : 0;
      }
      throw new RQLParserError(
        `Invalid boolean value for field '${fieldDef.name}': ${rawValue}. Use 'true' or 'false'`,
        position,
      );
    }

    throw new RQLParserError(
      `Unsupported field type: ${fieldDef.type}`,
      position,
    );
  }

  private match(type: string, value?: string): boolean {
    if (!this.check(type, value)) return false;
    this.advance();
    return true;
  }

  private check(type: string, value?: string): boolean {
    if (this.isAtEnd()) return false;
    const token = this.getCurrent();
    return (
      token.type === type && (value === undefined || token.value === value)
    );
  }

  private advance(): RQLToken {
    if (!this.isAtEnd()) this.currentIndex++;
    return this.previous();
  }

  public isAtEnd(): boolean {
    return this.currentIndex >= this.tokens.length;
  }

  public getCurrent(): RQLToken {
    return this.tokens[this.currentIndex];
  }

  private previous(): RQLToken {
    return this.tokens[this.currentIndex - 1];
  }
}
